import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Dropdown, { DropdownContent, DropdownTrigger } from 'react-simple-dropdown';
import { useDispatch, useSelector } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import moment from 'moment';

import { ConfirmModal } from '../../../common';
import { DSelect, HookForm, HInput, HDatePicker } from '../../../../validation/HForm';
import {
  cashBoxShema,
  delayPercentShema,
  basePercentShema,
  dealNumberShema,
  redemptionDateSchema,
} from './clientDropdownSchema';
import { getDepartments } from '../../../../redux/actions/handbookAction';
import { returnKeys } from '../../../../helpers/common';

import './clientDropdown.scss';

import CrossSmallIcon from '../../../../assets/icons/cross.svg';

const ClientDropdown = memo((props) => {
  let form = {};
  const {
    children,
    text,
    content,
    initValues,
    handleChangeContract,
    isShow,
    changeIsShow,
    viewDropdown,
  } = props;
  const { departments, loading } = useSelector(({ handbook }) => handbook);

  const minDate = moment().add(1, 'days').format('YYYY-MM-DD');
  const maxDate = moment().add(30, 'days').format('YYYY-MM-DD');

  const dispatch = useDispatch();

  const [state, setState] = useState({
    isOpenPopUp: false,
    isDismissButton: false,
    isApplyButton: false,
  });
  const dropdownContentRef = useRef();

  const handleOpenClose = useCallback(() => {
    if(returnKeys(form).length === 0) return
    
    const data = form.getValues();
    if (data?.delayPercent) data.delayPercent = +data.delayPercent;
    if (data?.basePercent) data.basePercent = +data.basePercent;
    if (_isEqual(initValues, data)) {
      setState((prev) => ({ ...prev, isOpenPopUp: false }));
      changeIsShow(returnKeys(initValues)[0]);
    } else {
      setState((prev) => ({ ...prev, isDismissButton: true }));
    }
  }, [initValues, form, changeIsShow]);

  const handleOutsideClick = useCallback(
    (e) => {
      const composed = e.composedPath()
      const confirmModal = document.getElementById('confirmModal');
      const isTextSelected = window.getSelection().toString() !== '';
      if (
        !composed.includes(dropdownContentRef.current) &&
        !composed.includes(confirmModal) &&
        state.isOpenPopUp && !isTextSelected
      ) {
        handleOpenClose();
      }
    },
    [state.isOpenPopUp, handleOpenClose],
  );


  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [state.isOpenPopUp, handleOutsideClick]);

  const handleShow = () => {
    const { dealNumber, delayPercent, cashBoxId, basePercent, redemptionDate } = isShow;
    if (!dealNumber && !delayPercent && !cashBoxId && !basePercent && !redemptionDate) {
      setState((prev) => ({ ...prev, isOpenPopUp: true }));
      changeIsShow(returnKeys(initValues)[0]);
    }
  };

  const handleHide = () => {
    setState((prev) => ({ ...prev, isOpenPopUp: false }));
  };

  const handleOpenChanges = () => {
    const data = form.getValues();
    if (data?.dealNumber) data.dealNumber = data.dealNumber.toUpperCase();
    if (data?.delayPercent) data.delayPercent = +data.delayPercent;
    if (data?.basePercent) data.basePercent = +data.basePercent;
    if (!_isEqual(initValues, data)) setState((prev) => ({ ...prev, isApplyButton: true }));
  };

  const handleLocalCloseModal = useCallback(() => {
    setState((prev) => ({ ...prev, isDismissButton: false, isApplyButton: false }));
  }, []);

  const handleOptionsDepartments = (searchQuery, action) => {
    if (action.action === 'input-change') dispatch(getDepartments(searchQuery));
  };

  const closePopUp = useCallback(() => {
    handleLocalCloseModal();
    changeIsShow(returnKeys(initValues)[0]);
    handleHide();
    form.reset();
  }, [changeIsShow, handleLocalCloseModal, initValues, form]);

  const applyChanges = useCallback(() => {
    handleChangeContract(form.getValues());
    closePopUp();
  }, [closePopUp, handleChangeContract, form]);

  const createContent = (view) => {
    switch (view) {
      case viewDropdown.dealNumber:
        return (
          <HInput
            h="true"
            name="dealNumber"
            type="text"
            className="input text-transform"
            id="dealNumber"
            defaultValue={initValues.dealNumber}
          />
        );
      case viewDropdown.delayPercent:
        return (
          <HInput
            h="true"
            name="delayPercent"
            type="text"
            className="input"
            id="delayPercent"
            decimals={2}
            defaultValue={initValues.delayPercent}
          />
        );
      case viewDropdown.basePercent:
        return (
          <HInput
            h="true"
            name="basePercent"
            type="text"
            className="input"
            id="basePercent"
            decimals={2}
            defaultValue={initValues.basePercent}
          />
        );
      case viewDropdown.cashBox:
        return (
          <DSelect
            h="true"
            name="cashBoxId"
            isLoading={loading}
            options={departments}
            defaultValue={initValues}
            handleOptions={handleOptionsDepartments}
            placeholder="Введите название"
          />
        );
      case viewDropdown.redemptionDate:
        return (
          <HDatePicker
            h="true"
            name="redemptionDate"
            id="redemptionDate"
            className="input prolong-input"
            min={minDate}
            max={maxDate}
            defaultValue={initValues || minDate}
          />
        );

      default:
        return null;
    }
  };

  const usedShema = (view) => {
    switch (view) {
      case viewDropdown.dealNumber:
        return dealNumberShema;
      case viewDropdown.delayPercent:
        return delayPercentShema;
      case viewDropdown.basePercent:
        return basePercentShema;
      case viewDropdown.cashBox:
        return cashBoxShema;
      case viewDropdown.redemptionDate:
        return redemptionDateSchema;

      default:
        break;
    }
  };

  return (
    <>
      <Dropdown
        className="dropdown-left dropdown-caret"
        active={state.isOpenPopUp}
        onClick={handleShow}>
        <DropdownTrigger className="link-dashed" href="#">
          {children}
        </DropdownTrigger>
        <DropdownContent>
          <div ref={dropdownContentRef} className="dropdown-content caret">
            {(isShow.dealNumber ||
              isShow.delayPercent ||
              isShow.cashBoxId ||
              isShow.basePercent ||
              isShow.redemptionDate) && (
              <HookForm
                validationSchema={usedShema(content)}
                defaultValues={initValues}
                predef={(methods) => (form = methods)}
                onSubmit={handleOpenChanges}>
                <div className="dropdown-content-data">
                  <p>{text}</p>
                  <div className="form-element m-t-spacer m-b-0">
                    <div className="form-element-data clientDropdown-form-element-data">
                      {createContent(content)}
                    </div>
                  </div>
                </div>
                <div className="dropdown-content-actions">
                  <div className="dropdown-content-actions-left">
                    <button type="submit" className="button-small">
                      <span>Применить</span>
                    </button>
                  </div>
                  <div className="dropdown-content-actions-right">
                    <button
                      type="button"
                      className="button-icontrigger noborder"
                      onClick={handleOpenClose}>
                      <span className="icon">
                        <CrossSmallIcon />
                      </span>
                    </button>
                  </div>
                </div>
              </HookForm>
            )}
          </div>
        </DropdownContent>
      </Dropdown>
      <ConfirmModal
        isOpen={state.isDismissButton}
        handleClose={handleLocalCloseModal}
        handleAction={closePopUp}
        headerText="Отменить изменения?"
        bodyText="Внесённые данные не сохранятся."
      />
      <ConfirmModal
        isOpen={state.isApplyButton}
        handleClose={closePopUp}
        handleAction={applyChanges}
        headerText="Применить изменения?"
        bodyText="Внесённые данные сохранятся."
      />
    </>
  );
});

export default ClientDropdown;
